'use client';

import { useState, useEffect } from 'react';
import TradeVolumeChart from './TradeVolumeChart';
import TradeFlowVisualization from './TradeFlowVisualization';
import EconomicIndicatorCard from './EconomicIndicatorCard';
import ExportOpportunityHeatMap from './ExportOpportunityHeatMap';
import ComparativeAnalysisTable from './ComparativeAnalysisTable';
import DownloadableReports from './DownloadableReports';
import Icon from '@/components/ui/AppIcon';

interface TradeVolumeData {
  year: string;
  exports: number;
  imports: number;
}

interface TradeFlowData {
  month: string;
  goldExports: number;
  silverExports: number;
  copperExports: number;
}

interface EconomicIndicator {
  title: string;
  value: string;
  change: string;
  trend: 'up' | 'down';
  icon: string;
  description: string;
}

interface OpportunityData {
  category: string;
  potential: 'high' | 'medium' | 'low';
  value: string;
  growth: string;
  description: string;
}

interface CountryData {
  country: string;
  tradeVolume: string;
  goldExports: string;
  marketShare: string;
  growthRate: string;
  flag: string;
}

interface Report {
  title: string;
  description: string;
  date: string;
  size: string;
  format: string;
  icon: string;
}

const TradeAnalyticsInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedTimeframe, setSelectedTimeframe] = useState('yearly');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const tradeVolumeData: TradeVolumeData[] = [
    { year: '2019', exports: 245, imports: 89 },
    { year: '2020', exports: 312, imports: 102 },
    { year: '2021', exports: 428, imports: 134 },
    { year: '2022', exports: 567, imports: 178 },
    { year: '2023', exports: 734, imports: 223 },
    { year: '2024', exports: 892, imports: 267 }
  ];

  const tradeFlowData: TradeFlowData[] = [
    { month: 'Jan', goldExports: 1250, silverExports: 340, copperExports: 45 },
    { month: 'Feb', goldExports: 1380, silverExports: 365, copperExports: 52 },
    { month: 'Mar', goldExports: 1520, silverExports: 398, copperExports: 58 },
    { month: 'Apr', goldExports: 1450, silverExports: 412, copperExports: 61 },
    { month: 'May', goldExports: 1680, silverExports: 445, copperExports: 67 },
    { month: 'Jun', goldExports: 1790, silverExports: 478, copperExports: 73 },
    { month: 'Jul', goldExports: 1850, silverExports: 502, copperExports: 78 },
    { month: 'Aug', goldExports: 1920, silverExports: 534, copperExports: 82 },
    { month: 'Sep', goldExports: 2010, silverExports: 567, copperExports: 89 },
    { month: 'Oct', goldExports: 2150, silverExports: 598, copperExports: 94 },
    { month: 'Nov', goldExports: 2280, silverExports: 623, copperExports: 98 },
    { month: 'Dec', goldExports: 2420, silverExports: 656, copperExports: 105 }
  ];

  const economicIndicators: EconomicIndicator[] = [
    {
      title: 'Total Trade Volume',
      value: '$1.16B',
      change: '+28.4%',
      trend: 'up',
      icon: 'ChartBarIcon',
      description: 'Year-over-year bilateral trade growth'
    },
    {
      title: 'Gold Export Value',
      value: '$892M',
      change: '+21.6%',
      trend: 'up',
      icon: 'CurrencyDollarIcon',
      description: 'Primary precious metals export revenue'
    },
    {
      title: 'Market Share in UAE',
      value: '34.7%',
      change: '+5.2%',
      trend: 'up',
      icon: 'ChartPieIcon',
      description: 'Uganda\'s share of UAE precious metals imports'
    },
    {
      title: 'Trade Balance',
      value: '$625M',
      change: '+18.9%',
      trend: 'up',
      icon: 'ScaleIcon',
      description: 'Positive trade surplus with UAE'
    }
  ];

  const exportOpportunities: OpportunityData[] = [
    {
      category: 'Refined Gold Bars',
      potential: 'high',
      value: '$450M',
      growth: '+32%',
      description: 'Investment-grade gold bars with LBMA certification showing strongest demand growth'
    },
    {
      category: 'Silver Bullion',
      potential: 'high',
      value: '$180M',
      growth: '+28%',
      description: 'Industrial and investment silver with increasing UAE manufacturing demand'
    },
    {
      category: 'Copper Cathodes',
      potential: 'medium',
      value: '$95M',
      growth: '+15%',
      description: 'High-purity copper for UAE construction and electronics sectors'
    },
    {
      category: 'Gold Jewelry Components',
      potential: 'high',
      value: '$220M',
      growth: '+24%',
      description: 'Semi-finished gold products for Dubai\'s jewelry manufacturing hub'
    },
    {
      category: 'Platinum Group Metals',
      potential: 'medium',
      value: '$65M',
      growth: '+19%',
      description: 'Emerging market for automotive and industrial applications'
    },
    {
      category: 'Rare Earth Elements',
      potential: 'low',
      value: '$28M',
      growth: '+8%',
      description: 'Niche market with potential for technology sector growth'
    }
  ];

  const comparativeData: CountryData[] = [
    {
      country: 'Uganda',
      tradeVolume: '$1.16B',
      goldExports: '892M kg',
      marketShare: '34.7%',
      growthRate: '+28.4%',
      flag: '🇺🇬'
    },
    {
      country: 'South Africa',
      tradeVolume: '$2.34B',
      goldExports: '1.8B kg',
      marketShare: '42.3%',
      growthRate: '+18.2%',
      flag: '🇿🇦'
    },
    {
      country: 'Ghana',
      tradeVolume: '$890M',
      goldExports: '678M kg',
      marketShare: '28.5%',
      growthRate: '+22.7%',
      flag: '🇬🇭'
    },
    {
      country: 'Tanzania',
      tradeVolume: '$745M',
      goldExports: '567M kg',
      marketShare: '24.1%',
      growthRate: '+19.8%',
      flag: '🇹🇿'
    },
    {
      country: 'Kenya',
      tradeVolume: '$512M',
      goldExports: '389M kg',
      marketShare: '18.9%',
      growthRate: '+15.3%',
      flag: '🇰🇪'
    }
  ];

  const reports: Report[] = [
    {
      title: 'Uganda-UAE Trade Relationship 2024 Annual Report',
      description: 'Comprehensive analysis of bilateral trade flows, market trends, and growth opportunities in precious metals sector',
      date: 'December 2024',
      size: '4.2 MB',
      format: 'PDF',
      icon: 'DocumentChartBarIcon'
    },
    {
      title: 'Q4 2024 Precious Metals Export Analysis',
      description: 'Quarterly breakdown of gold, silver, and copper exports with price trends and volume analysis',
      date: 'November 2024',
      size: '2.8 MB',
      format: 'PDF',
      icon: 'DocumentTextIcon'
    },
    {
      title: 'UAE Market Opportunity Assessment',
      description: 'Strategic analysis of emerging opportunities in UAE precious metals market with actionable insights',
      date: 'October 2024',
      size: '3.5 MB',
      format: 'PDF',
      icon: 'PresentationChartLineIcon'
    },
    {
      title: 'East African Competitive Landscape Report',
      description: 'Comparative analysis of Uganda\'s position versus regional competitors in UAE market',
      date: 'September 2024',
      size: '3.1 MB',
      format: 'PDF',
      icon: 'ChartBarSquareIcon'
    }
  ];

  if (!isHydrated) {
    return (
      <div data-component-id="src/app/uganda-uae-trade-analytics/components/TradeAnalyticsInteractive.tsx:252:6" data-component-path="src/app/uganda-uae-trade-analytics/components/TradeAnalyticsInteractive.tsx" data-component-line="252" data-component-file="TradeAnalyticsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
        <div data-component-id="src/app/uganda-uae-trade-analytics/components/TradeAnalyticsInteractive.tsx:253:8" data-component-path="src/app/uganda-uae-trade-analytics/components/TradeAnalyticsInteractive.tsx" data-component-line="253" data-component-file="TradeAnalyticsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-full%20px-4%20lg%3Apx-8%20py-8%22%7D" className="w-full px-4 lg:px-8 py-8">
          <div data-component-id="src/app/uganda-uae-trade-analytics/components/TradeAnalyticsInteractive.tsx:254:10" data-component-path="src/app/uganda-uae-trade-analytics/components/TradeAnalyticsInteractive.tsx" data-component-line="254" data-component-file="TradeAnalyticsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22animate-pulse%20space-y-8%22%7D" className="animate-pulse space-y-8">
            <div data-component-id="src/app/uganda-uae-trade-analytics/components/TradeAnalyticsInteractive.tsx:255:12" data-component-path="src/app/uganda-uae-trade-analytics/components/TradeAnalyticsInteractive.tsx" data-component-line="255" data-component-file="TradeAnalyticsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-12%20bg-surface%20rounded-lg%20w-1%2F3%22%7D" className="h-12 bg-surface rounded-lg w-1/3"></div>
            <div data-component-id="src/app/uganda-uae-trade-analytics/components/TradeAnalyticsInteractive.tsx:256:12" data-component-path="src/app/uganda-uae-trade-analytics/components/TradeAnalyticsInteractive.tsx" data-component-line="256" data-component-file="TradeAnalyticsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-2%20lg%3Agrid-cols-4%20gap-6%22%7D" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[1, 2, 3, 4].map((i) => (
                <div data-component-id="src/app/uganda-uae-trade-analytics/components/TradeAnalyticsInteractive.tsx:258:16" data-component-path="src/app/uganda-uae-trade-analytics/components/TradeAnalyticsInteractive.tsx" data-component-line="258" data-component-file="TradeAnalyticsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-40%20bg-surface%20rounded-xl%22%7D" key={i} className="h-40 bg-surface rounded-xl"></div>
              ))}
            </div>
            <div data-component-id="src/app/uganda-uae-trade-analytics/components/TradeAnalyticsInteractive.tsx:261:12" data-component-path="src/app/uganda-uae-trade-analytics/components/TradeAnalyticsInteractive.tsx" data-component-line="261" data-component-file="TradeAnalyticsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-96%20bg-surface%20rounded-xl%22%7D" className="h-96 bg-surface rounded-xl"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-component-id="src/app/uganda-uae-trade-analytics/components/TradeAnalyticsInteractive.tsx:269:4" data-component-path="src/app/uganda-uae-trade-analytics/components/TradeAnalyticsInteractive.tsx" data-component-line="269" data-component-file="TradeAnalyticsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-full%20space-y-8%22%7D" className="w-full space-y-8">
      <div data-component-id="src/app/uganda-uae-trade-analytics/components/TradeAnalyticsInteractive.tsx:270:6" data-component-path="src/app/uganda-uae-trade-analytics/components/TradeAnalyticsInteractive.tsx" data-component-line="270" data-component-file="TradeAnalyticsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-col%20lg%3Aflex-row%20lg%3Aitems-center%20lg%3Ajustify-between%20gap-4%22%7D" className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div data-component-id="src/app/uganda-uae-trade-analytics/components/TradeAnalyticsInteractive.tsx:271:8" data-component-path="src/app/uganda-uae-trade-analytics/components/TradeAnalyticsInteractive.tsx" data-component-line="271" data-component-file="TradeAnalyticsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
          <h1 data-component-id="src/app/uganda-uae-trade-analytics/components/TradeAnalyticsInteractive.tsx:272:10" data-component-path="src/app/uganda-uae-trade-analytics/components/TradeAnalyticsInteractive.tsx" data-component-line="272" data-component-file="TradeAnalyticsInteractive.tsx" data-component-name="h1" data-component-content="%7B%22elementName%22%3A%22h1%22%2C%22className%22%3A%22text-3xl%20lg%3Atext-4xl%20font-bold%20text-text-primary%20font-accent%20mb-2%22%2C%22textContent%22%3A%22Uganda-UAE%20Trade%20Analytics%22%7D" className="text-3xl lg:text-4xl font-bold text-text-primary font-accent mb-2">
            Uganda-UAE Trade Analytics
          </h1>
          <p data-component-id="src/app/uganda-uae-trade-analytics/components/TradeAnalyticsInteractive.tsx:275:10" data-component-path="src/app/uganda-uae-trade-analytics/components/TradeAnalyticsInteractive.tsx" data-component-line="275" data-component-file="TradeAnalyticsInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-text-secondary%22%2C%22textContent%22%3A%22Comprehensive%20bilateral%20trade%20insights%20and%20market%20intelligence%20for%20precious%20metals%20sector%22%7D" className="text-text-secondary">
            Comprehensive bilateral trade insights and market intelligence for precious metals sector
          </p>
        </div>
        <div data-component-id="src/app/uganda-uae-trade-analytics/components/TradeAnalyticsInteractive.tsx:279:8" data-component-path="src/app/uganda-uae-trade-analytics/components/TradeAnalyticsInteractive.tsx" data-component-line="279" data-component-file="TradeAnalyticsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20space-x-3%22%7D" className="flex items-center space-x-3">
          <button data-component-id="src/app/uganda-uae-trade-analytics/components/TradeAnalyticsInteractive.tsx:280:10" data-component-path="src/app/uganda-uae-trade-analytics/components/TradeAnalyticsInteractive.tsx" data-component-line="280" data-component-file="TradeAnalyticsInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22textContent%22%3A%22Monthly%22%7D"
            onClick={() => setSelectedTimeframe('monthly')}
            className={`px-4 py-2 text-sm font-semibold rounded-lg transition-smooth ${
              selectedTimeframe === 'monthly' ?'bg-primary text-primary-foreground' :'bg-surface text-text-secondary hover:bg-muted'
            }`}
          >
            Monthly
          </button>
          <button data-component-id="src/app/uganda-uae-trade-analytics/components/TradeAnalyticsInteractive.tsx:288:10" data-component-path="src/app/uganda-uae-trade-analytics/components/TradeAnalyticsInteractive.tsx" data-component-line="288" data-component-file="TradeAnalyticsInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22textContent%22%3A%22Quarterly%22%7D"
            onClick={() => setSelectedTimeframe('quarterly')}
            className={`px-4 py-2 text-sm font-semibold rounded-lg transition-smooth ${
              selectedTimeframe === 'quarterly' ?'bg-primary text-primary-foreground' :'bg-surface text-text-secondary hover:bg-muted'
            }`}
          >
            Quarterly
          </button>
          <button data-component-id="src/app/uganda-uae-trade-analytics/components/TradeAnalyticsInteractive.tsx:296:10" data-component-path="src/app/uganda-uae-trade-analytics/components/TradeAnalyticsInteractive.tsx" data-component-line="296" data-component-file="TradeAnalyticsInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22textContent%22%3A%22Yearly%22%7D"
            onClick={() => setSelectedTimeframe('yearly')}
            className={`px-4 py-2 text-sm font-semibold rounded-lg transition-smooth ${
              selectedTimeframe === 'yearly' ?'bg-primary text-primary-foreground' :'bg-surface text-text-secondary hover:bg-muted'
            }`}
          >
            Yearly
          </button>
        </div>
      </div>

      <div data-component-id="src/app/uganda-uae-trade-analytics/components/TradeAnalyticsInteractive.tsx:307:6" data-component-path="src/app/uganda-uae-trade-analytics/components/TradeAnalyticsInteractive.tsx" data-component-line="307" data-component-file="TradeAnalyticsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-2%20lg%3Agrid-cols-4%20gap-6%22%7D" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {economicIndicators.map((indicator, index) => (
          <EconomicIndicatorCard data-component-id="src/app/uganda-uae-trade-analytics/components/TradeAnalyticsInteractive.tsx:309:10" data-component-path="src/app/uganda-uae-trade-analytics/components/TradeAnalyticsInteractive.tsx" data-component-line="309" data-component-file="TradeAnalyticsInteractive.tsx" data-component-name="EconomicIndicatorCard" data-component-content="%7B%22elementName%22%3A%22EconomicIndicatorCard%22%2C%22%5Bspread%5D%22%3A%22true%22%7D" key={index} {...indicator} />
        ))}
      </div>

      <div data-component-id="src/app/uganda-uae-trade-analytics/components/TradeAnalyticsInteractive.tsx:313:6" data-component-path="src/app/uganda-uae-trade-analytics/components/TradeAnalyticsInteractive.tsx" data-component-line="313" data-component-file="TradeAnalyticsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20lg%3Agrid-cols-2%20gap-6%22%7D" className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <TradeVolumeChart data-component-id="src/app/uganda-uae-trade-analytics/components/TradeAnalyticsInteractive.tsx:314:8" data-component-path="src/app/uganda-uae-trade-analytics/components/TradeAnalyticsInteractive.tsx" data-component-line="314" data-component-file="TradeAnalyticsInteractive.tsx" data-component-name="TradeVolumeChart" data-component-content="%7B%22elementName%22%3A%22TradeVolumeChart%22%7D" data={tradeVolumeData} />
        <TradeFlowVisualization data-component-id="src/app/uganda-uae-trade-analytics/components/TradeAnalyticsInteractive.tsx:315:8" data-component-path="src/app/uganda-uae-trade-analytics/components/TradeAnalyticsInteractive.tsx" data-component-line="315" data-component-file="TradeAnalyticsInteractive.tsx" data-component-name="TradeFlowVisualization" data-component-content="%7B%22elementName%22%3A%22TradeFlowVisualization%22%7D" data={tradeFlowData} />
      </div>

      <ExportOpportunityHeatMap data-component-id="src/app/uganda-uae-trade-analytics/components/TradeAnalyticsInteractive.tsx:318:6" data-component-path="src/app/uganda-uae-trade-analytics/components/TradeAnalyticsInteractive.tsx" data-component-line="318" data-component-file="TradeAnalyticsInteractive.tsx" data-component-name="ExportOpportunityHeatMap" data-component-content="%7B%22elementName%22%3A%22ExportOpportunityHeatMap%22%7D" opportunities={exportOpportunities} />

      <ComparativeAnalysisTable data-component-id="src/app/uganda-uae-trade-analytics/components/TradeAnalyticsInteractive.tsx:320:6" data-component-path="src/app/uganda-uae-trade-analytics/components/TradeAnalyticsInteractive.tsx" data-component-line="320" data-component-file="TradeAnalyticsInteractive.tsx" data-component-name="ComparativeAnalysisTable" data-component-content="%7B%22elementName%22%3A%22ComparativeAnalysisTable%22%7D" data={comparativeData} />

      <DownloadableReports data-component-id="src/app/uganda-uae-trade-analytics/components/TradeAnalyticsInteractive.tsx:322:6" data-component-path="src/app/uganda-uae-trade-analytics/components/TradeAnalyticsInteractive.tsx" data-component-line="322" data-component-file="TradeAnalyticsInteractive.tsx" data-component-name="DownloadableReports" data-component-content="%7B%22elementName%22%3A%22DownloadableReports%22%7D" reports={reports} />

      <div data-component-id="src/app/uganda-uae-trade-analytics/components/TradeAnalyticsInteractive.tsx:324:6" data-component-path="src/app/uganda-uae-trade-analytics/components/TradeAnalyticsInteractive.tsx" data-component-line="324" data-component-file="TradeAnalyticsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-gradient-to-r%20from-primary%2F10%20to-secondary%2F10%20rounded-xl%20p-8%20border%20border-primary%2F20%22%7D" className="bg-gradient-to-r from-primary/10 to-secondary/10 rounded-xl p-8 border border-primary/20">
        <div data-component-id="src/app/uganda-uae-trade-analytics/components/TradeAnalyticsInteractive.tsx:325:8" data-component-path="src/app/uganda-uae-trade-analytics/components/TradeAnalyticsInteractive.tsx" data-component-line="325" data-component-file="TradeAnalyticsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-col%20lg%3Aflex-row%20items-center%20justify-between%20gap-6%22%7D" className="flex flex-col lg:flex-row items-center justify-between gap-6">
          <div data-component-id="src/app/uganda-uae-trade-analytics/components/TradeAnalyticsInteractive.tsx:326:10" data-component-path="src/app/uganda-uae-trade-analytics/components/TradeAnalyticsInteractive.tsx" data-component-line="326" data-component-file="TradeAnalyticsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-start%20space-x-4%22%7D" className="flex items-start space-x-4">
            <div data-component-id="src/app/uganda-uae-trade-analytics/components/TradeAnalyticsInteractive.tsx:327:12" data-component-path="src/app/uganda-uae-trade-analytics/components/TradeAnalyticsInteractive.tsx" data-component-line="327" data-component-file="TradeAnalyticsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-12%20h-12%20rounded-lg%20golden-gradient%20flex%20items-center%20justify-center%20shadow-premium%20flex-shrink-0%22%7D" className="w-12 h-12 rounded-lg golden-gradient flex items-center justify-center shadow-premium flex-shrink-0">
              <Icon data-component-id="src/app/uganda-uae-trade-analytics/components/TradeAnalyticsInteractive.tsx:328:14" data-component-path="src/app/uganda-uae-trade-analytics/components/TradeAnalyticsInteractive.tsx" data-component-line="328" data-component-file="TradeAnalyticsInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22LightBulbIcon%22%2C%22className%22%3A%22text-primary-foreground%22%7D" name="LightBulbIcon" size={24} variant="solid" className="text-primary-foreground" />
            </div>
            <div data-component-id="src/app/uganda-uae-trade-analytics/components/TradeAnalyticsInteractive.tsx:330:12" data-component-path="src/app/uganda-uae-trade-analytics/components/TradeAnalyticsInteractive.tsx" data-component-line="330" data-component-file="TradeAnalyticsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
              <h3 data-component-id="src/app/uganda-uae-trade-analytics/components/TradeAnalyticsInteractive.tsx:331:14" data-component-path="src/app/uganda-uae-trade-analytics/components/TradeAnalyticsInteractive.tsx" data-component-line="331" data-component-file="TradeAnalyticsInteractive.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22text-xl%20font-bold%20text-text-primary%20mb-2%22%2C%22textContent%22%3A%22Need%20Custom%20Market%20Analysis%3F%22%7D" className="text-xl font-bold text-text-primary mb-2">Need Custom Market Analysis?</h3>
              <p data-component-id="src/app/uganda-uae-trade-analytics/components/TradeAnalyticsInteractive.tsx:332:14" data-component-path="src/app/uganda-uae-trade-analytics/components/TradeAnalyticsInteractive.tsx" data-component-line="332" data-component-file="TradeAnalyticsInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-text-secondary%22%2C%22textContent%22%3A%22Our%20market%20intelligence%20team%20can%20provide%20tailored%20insights%20and%20forecasting%20for%20your%20specific%20trading%20needs%22%7D" className="text-text-secondary">
                Our market intelligence team can provide tailored insights and forecasting for your specific trading needs
              </p>
            </div>
          </div>
          <button data-component-id="src/app/uganda-uae-trade-analytics/components/TradeAnalyticsInteractive.tsx:337:10" data-component-path="src/app/uganda-uae-trade-analytics/components/TradeAnalyticsInteractive.tsx" data-component-line="337" data-component-file="TradeAnalyticsInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22px-6%20py-3%20text-sm%20font-semibold%20text-primary-foreground%20bg-primary%20rounded-lg%20hover%3Abg-primary%2F90%20hover%3Ashadow-md%20hover%3A-translate-y-0.5%20transition-smooth%20whitespace-nowrap%22%2C%22textContent%22%3A%22Request%20Custom%20Report%22%7D" className="px-6 py-3 text-sm font-semibold text-primary-foreground bg-primary rounded-lg hover:bg-primary/90 hover:shadow-md hover:-translate-y-0.5 transition-smooth whitespace-nowrap">
            Request Custom Report
          </button>
        </div>
      </div>
    </div>
  );
};

export default TradeAnalyticsInteractive;